
import {
	AddDataSharedFunc, DashboardSharedFilterFunc, DashboardSharedFilterKey,
	IDashboardSharedChartData, IDashboardSharedDimensionsViewModel,
} from "models/common";
import { IFilters, IFilterKeys, DimensionsById, ISourceData, IDimFacility, IDimension, IDimMDT } from "models/core";
import { IDashboardViewModel } from "./viewModel";

//Plots for this dashboard
import * as PrimarySite from "./primarySiteHierarchy";
import * as Morphology from "./morphology";
import * as Remoteness from "../../common/plotdata/remoteness";
import * as HHSOfResidence from "../../common/plotdata/hhsOfResidence";

export type { IDashboardViewModel } from "./viewModel";

export type IDashboardSource = IDashboardViewModel[];

export interface ILungIndicatorConstants {
	totalLungDiagnoses: number,
	upper95Percent: number,
	lower95Percent: number,
	upper98Percent: number,
	lower98Percent: number,
	statePercent: number
}

export interface IDashboardDimensionsViewModel extends IDashboardSharedDimensionsViewModel {
	defMastectomyArray?: Array<ILungIndicatorConstants>;
};

interface ILevel1ChartData {
	totalDiagnoses: number;
	totalDataQualityEligibleDiagnoses: number;
	facilities: IDimFacility[];
	hhses: IDimension[];
	mdts: IDimMDT[];
	month: IDimension[];
	year: IDimension[];

	hadSurgery: { numerator, denominator: number, percentage: number };
    hadSurgeryStageOneOrTwo: { numerator, denominator: number, percentage: number };
    rtInoperableStageOneOrTwo: { numerator, denominator: number, percentage: number };
    //crtInoperableStageThree: { numerator, denominator: number, percentage: number };
    concurrentCRTInoperableStageThree: { numerator, denominator: number, percentage: number };
    //sequentialCRTInoperableStageThree: { numerator, denominator: number, percentage: number };
    lengthOfStay: { min: number, max: number, median: number };
    //emergencyReadmission30Day: { numerator, denominator: number, percentage: number };
    inHospitalMortality: { numerator, denominator: number, percentage: number };
    mortality30Day: { numerator, denominator: number, percentage: number };
    mortality90Day: { numerator, denominator: number, percentage: number };
    prolongedLOS: { numerator, denominator: number, percentage: number };
    mortality30DayRT: { numerator, denominator: number, percentage: number };
    mortality30DayIVST: { numerator, denominator: number, percentage: number };
    treatment30DaysFromDiagnosis: { numerator, denominator: number, percentage: number };
    surgery30DaysFromDiagnosis: { numerator, denominator: number, percentage: number };
    rt30DaysFromDiagnosis: { numerator, denominator: number, percentage: number };
    ivst30DaysFromDiagnosis: { numerator, denominator: number, percentage: number };
    survivalOneYearAfterSurgery: { numerator, denominator: number, percentage: number };
    survivalTwoYearAfterSurgery: { numerator, denominator: number, percentage: number };
	neoAdjOrAdjInStage2A3A: { numerator, denominator: number, percentage: number };
	receivedSTStage4: { numerator, denominator: number, percentage: number };
	hadPathDiagnosis: { numerator, denominator: number, percentage: number };
	treatmentRate: { numerator, denominator: number, percentage: number };
	hadSurgOrRTStageOneOrTwo: { numerator, denominator: number, percentage: number };
	// Export
	filteredReferralIdCsv: string;
}

// HACK: chop out the reference array from the shared interface 
type RestrictedSharedChartData = Omit<Omit<IDashboardSharedChartData, "facilities">, "hhses">;

interface ILevel2ChartData extends RestrictedSharedChartData {
	totalDiagnoses: number;
	primarySite: PrimarySite.IHierarchyPlotData;
	morphology: Morphology.IPlotData;
	remoteness: Remoteness.IPlotData;
	hhsOfResidence: HHSOfResidence.IPlotData;
}

export interface IChartData {
	level1: ILevel1ChartData;
	level2: ILevel2ChartData;
}

const LungFilterKeys: IFilterKeys = {
	//Filter keys for lung indicators
	DiagnosisYear: { key: "DiagnosisYear", label: "Diagnosis Year" },
	DiagnosisMonth: { key: "DiagnosisMonth", label: "Diagnosis Month" },
	MdtYear: { key: "MdtYear", label: "MDT Year" },
	MdtMonth: { key: "MdtMonth", label: "MDT Month" },
	PrimarySite: { key: "PrimarySite", label: "Primary site" },
	Morphology: { key: "Morphology", label: "Morphology" },
	Remoteness: { key: "Remoteness", label: "Rurality" },
	HHSOfResidence: { key: "HHSOfResidence", label: "HHS of residence" }
}

export const DashboardFilterKeys: IFilterKeys = {
	...DashboardSharedFilterKey,
	...LungFilterKeys
}

export type Filters = IFilters<typeof DashboardFilterKeys>;
export type DashboardFilterFunc = DashboardSharedFilterFunc<IDashboardViewModel>;
export type AddDataFunc = AddDataSharedFunc<IDashboardViewModel, ISourceData>;